import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pscDashboardApi } from '@/domains/traffic-monitoring/apis/psc-dashboard';
import { readBlobErrorMessage, saveBlob } from '@/shared/utils/download';

// PSC_PC0202002_BE_009 / BE_010. Mirrors traffic-monitoring's useTrafficExport
// shape (isExporting/errorMessage/clearError/runExport) but simpler - Report
// Management's filter row already carries the date range, so there's no
// modal/interval step here, just a direct call on click.
export type ReportExportFormat = 'id' | 'en';

const EXPORTS: Record<
  ReportExportFormat,
  { request: (params: { date_from: string; date_to: string }) => Promise<Blob>; filePrefix: string }
> = {
  id: { request: pscDashboardApi.exportReportsIndonesian, filePrefix: 'laporan-manajemen' },
  en: { request: pscDashboardApi.exportReportsEnglish, filePrefix: 'management-report' },
};

// Same message keys the backend can answer an export request with (see
// export_reports.py / daily_matrix_workbook.py's shared "msg > export ..."
// convention) - kept in step with useTrafficExport.ts's own fallback copy.
const ERROR_FALLBACKS: Record<string, string> = {
  'msg > date_from and date_to are required': 'Export failed, a date range is required.',
  'msg > export data limit exceeded':
    'Export Failed, data limit exceeded. Please select a shorter date range.',
  'msg > export template missing': 'Export failed, the report template is unavailable.',
};
const GENERIC_ERROR = 'Export failed. Please try again.';

const compact = (isoDate: string) => isoDate.replace(/-/g, '');

export const useReportExport = (format: ReportExportFormat) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const clearError = useCallback(() => setErrorMessage(null), []);

  const runExport = useCallback(
    async (dateFrom: string, dateTo: string) => {
      const { request, filePrefix } = EXPORTS[format];
      setIsExporting(true);
      setErrorMessage(null);
      try {
        const blob = await request({ date_from: dateFrom, date_to: dateTo });
        saveBlob(blob, `${filePrefix}_${compact(dateFrom)}-${compact(dateTo)}.xlsx`);
        return true;
      } catch (error) {
        const serverMessage = await readBlobErrorMessage(error);
        setErrorMessage(
          serverMessage
            ? t(serverMessage, ERROR_FALLBACKS[serverMessage] ?? GENERIC_ERROR)
            : t('msg > export failed', GENERIC_ERROR)
        );
        return false;
      } finally {
        setIsExporting(false);
      }
    },
    [format, t]
  );

  return { runExport, isExporting, errorMessage, clearError };
};
